import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../utils/cn";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useSpecialButtons } from "../specialButtons/SpecialButtonsContext";
import { haptic as fireHaptic, resolveHaptic, type HapticPattern } from "../../utils/haptics";
import { IosHapticOverlay } from "./IosHapticOverlay";

// helper to calculate luminance for contrast
const getLuminance = (hex: string): number => {
  const rgb = hex.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
  if (!rgb) return 0.5;
  const r = parseInt(rgb[1], 16) / 255;
  const g = parseInt(rgb[2], 16) / 255;
  const b = parseInt(rgb[3], 16) / 255;
  const [rs, gs, bs] = [r, g, b].map(val =>
    val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4)
  );
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

// helper to determine text color based on background
const getContrastTextColor = (bgHex: string): string => {
  const luminance = getLuminance(bgHex);
  return luminance > 0.5 ? '#232323' : '#ffffff';
};

// helper to lighten/darken hex color for hover
const adjustColor = (hex: string, amount: number): string => {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.max(0, Math.min(255, ((num >> 16) & 0xff) + amount));
  const g = Math.max(0, Math.min(255, ((num >> 8) & 0xff) + amount));
  const b = Math.max(0, Math.min(255, (num & 0xff) + amount));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
};

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-sm text-sm font-medium ring-offset-background transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-mint/90 text-ink border-brutal border-ink shadow-brutal-sm hover:bg-mint-hover/90 hover:shadow-sm hover:translate-y-[1px] hover:translate-x-[1px]",
        destructive: "bg-pink/90 text-ink border-brutal border-ink shadow-brutal-sm hover:bg-pink-hover/90 hover:shadow-sm hover:translate-y-[1px] hover:translate-x-[1px]",
        outline: "border-brutal border-ink bg-background hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-gold/90 text-ink border-brutal border-ink shadow-brutal-sm hover:bg-gold-hover/90 hover:shadow-sm hover:translate-y-[1px] hover:translate-x-[1px]",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-foreground underline-offset-4 hover:underline",
        sky: "bg-sky/90 text-ink border-brutal border-ink shadow-brutal-sm hover:bg-sky-hover/90 hover:shadow-sm hover:translate-y-[1px] hover:translate-x-[1px]",
        lemon: "bg-lemon/90 text-ink border-brutal border-ink shadow-brutal-sm hover:bg-lemon-hover/90 hover:shadow-sm hover:translate-y-[1px] hover:translate-x-[1px]",
        basic: "bg-mint/90 text-ink border-brutal border-ink shadow-brutal-sm hover:bg-mint-hover/90 hover:shadow-sm hover:translate-y-[1px] hover:translate-x-[1px]",
        yellow: "bg-lemon/90 text-ink border-brutal border-ink shadow-brutal-sm hover:bg-lemon-hover/90 hover:shadow-sm hover:translate-y-[1px] hover:translate-x-[1px]",
        soft: "rounded-xl bg-gray-100/80 text-gray-600 hover:bg-gray-200/80 hover:text-gray-800",
        softPrimary: "rounded-xl bg-mint/70 text-ink hover:bg-mint/90",
        custom: ""
      },
      size: {
        default: "h-11 px-4 py-2",
        sm: "h-9 rounded-sm px-3",
        lg: "h-11 rounded-sm px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  icon?: IconProp;
  iconPosition?: 'left' | 'right';
  bgColor?: string;
  hoverBgColor?: string;
  textColor?: string;
  borderColor?: string;
  loading?: boolean;
  mobileSlot?: 'special1' | 'special2' | 'special3';
  showText?: boolean;
  /**
   * Tactile feedback on press. Defaults to a light tap; pass `false` to silence
   * (e.g. very frequent/secondary buttons) or a named pattern such as
   * `"medium"` / `"success"` to give an important action a stronger cue.
   * Android uses the Vibration API. On iOS the press only buzzes with
   * `iosTapOverlay`. No-op elsewhere or when the user has disabled haptics
   * in their profile.
   */
  haptic?: boolean | HapticPattern;
  /**
   * Put a transparent switch under the finger so the press buzzes on iOS
   * (see IosHapticOverlay). Off by default because a pan that starts on that
   * switch never scrolls, and on a phone most Buttons sit in a scrolling page,
   * list or sheet. Opt in only for controls in chrome that never scrolls, like
   * a sheet footer or a chat composer. Needs an explicit `type="button"`: the
   * switch takes over the tap's activation, so a submit button would stop
   * submitting, and a button with no type is a submit button inside a form.
   * Ignored with `asChild` or `haptic={false}`.
   */
  iosTapOverlay?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, icon, iconPosition = 'left', bgColor, hoverBgColor, textColor, borderColor, style, loading, children, disabled, mobileSlot, onClick, showText = true, haptic, iosTapOverlay = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    const specialButtonsContext = useSpecialButtons();
    const [isMobile, setIsMobile] = React.useState(false);

    // extract stable register function to avoid depending on entire context object
    const register = specialButtonsContext?.register;

    // extract aria-label from props to avoid including entire props object in deps
    const ariaLabel = props['aria-label'];

    // check if we're on mobile
    React.useEffect(() => {
      const checkMobile = () => {
        setIsMobile(window.innerWidth <= 700);
      };
      checkMobile();
      window.addEventListener("resize", checkMobile);
      return () => window.removeEventListener("resize", checkMobile);
    }, []);

    // fire haptic feedback (light tap by default) then delegate to the caller's
    // onClick. Used directly on the rendered button below.
    const handleClick = React.useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        const pattern = resolveHaptic(haptic);
        if (pattern) fireHaptic(pattern);
        onClick?.(e);
      },
      [haptic, onClick]
    );

    // the mobile special slot renders elsewhere and reads the latest onClick
    // through this ref. Callers mostly pass inline handlers, and keying the
    // registration on their identity re-registered the slot (a provider state
    // update) on every parent render, which kept a render loop on /coffee-rate
    // alive while the add form was open.
    const onClickRef = React.useRef(onClick);
    onClickRef.current = onClick;
    const hasOnClick = !!onClick;

    const wrappedOnClick = React.useCallback(() => {
      const latestOnClick = onClickRef.current;
      if (latestOnClick) {
        const pattern = resolveHaptic(haptic);
        if (pattern) fireHaptic(pattern);
        // create minimal event object
        const evt = {
          preventDefault: () => { },
          stopPropagation: () => { },
        } as unknown as React.MouseEvent<HTMLButtonElement>;
        latestOnClick(evt);
      }
    }, [haptic]);

    // depend on the text label, not `children`, so JSX children don't
    // re-register the slot on every render either
    const label = typeof children === 'string' ? children : undefined;

    // memoize config to avoid unnecessary re-registrations
    // don't gate on specialButtonsContext here - gate the effect instead
    // only create config if we have onClick (don't register no-op buttons)
    const config = React.useMemo(() => {
      if (!mobileSlot || !isMobile || !hasOnClick) {
        return null;
      }

      return {
        label,
        icon: icon,
        onClick: wrappedOnClick,
        disabled: disabled || loading,
        ariaLabel: ariaLabel || label,
        variant: (variant as string | null) ?? undefined,
        bgColor: bgColor,
        className: className,
        showText: showText,
      };
    }, [mobileSlot, isMobile, hasOnClick, label, icon, wrappedOnClick, disabled, loading, ariaLabel, variant, bgColor, className, showText]);

    // register special button if mobileSlot is set and we're on mobile
    // gate on register function existing, not the entire context object
    React.useEffect(() => {
      if (!register || !config || !mobileSlot) {
        return;
      }
      register(mobileSlot, config);
      // cleanup on unmount
      return () => {
        register(mobileSlot, null);
      };
    }, [register, mobileSlot, config]);

    // if mobileSlot is set and we're on mobile, don't render in-page
    if (mobileSlot && isMobile) {
      return null;
    }

    // calculate colors if bgColor is provided
    const finalBgColor = bgColor || undefined;
    const finalHoverBgColor = hoverBgColor || (bgColor ? adjustColor(bgColor, -30) : undefined);
    const finalTextColor = textColor || (bgColor ? getContrastTextColor(bgColor) : undefined);
    const finalBorderColor = borderColor || bgColor || undefined;

    // build CSS variables and override classes
    const customStyles: React.CSSProperties & Record<string, string> = {};
    const overrideClasses: string[] = [];

    if (finalBgColor) {
      customStyles['--btn-bg'] = finalBgColor;
      overrideClasses.push('bg-[var(--btn-bg)]');
    }
    if (finalHoverBgColor) {
      customStyles['--btn-bg-hover'] = finalHoverBgColor;
      overrideClasses.push('hover:bg-[var(--btn-bg-hover)]');
    }
    if (finalTextColor) {
      customStyles['--btn-text'] = finalTextColor;
      overrideClasses.push('text-[color:var(--btn-text)]');
    }
    if (finalBorderColor) {
      customStyles['--btn-border'] = finalBorderColor;
      overrideClasses.push('border-[color:var(--btn-border)]');
    }

    // when asChild is true, Slot expects exactly one child
    // so we only pass children, not loading spinner or icons
    if (asChild) {
      return (
        <Comp
          className={cn(buttonVariants({ variant, size }), overrideClasses, className)}
          ref={ref}
          style={{ ...customStyles, ...style }}
          disabled={disabled || loading}
          onClick={handleClick}
          {...props}
        >
          {children}
        </Comp>
      );
    }

    // iOS buzzes only when the finger lands on a real switch, and a pan that
    // starts on that switch never scrolls, so the overlay is opt-in. It also
    // needs an explicit type="button": the switch wins the activation, and a
    // button with no type submits its form.
    const wantsIosOverlay = iosTapOverlay && props.type === "button" && resolveHaptic(haptic) !== null;

    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size }),
          overrideClasses,
          wantsIosOverlay && "ios-haptic-host",
          className,
        )}
        ref={ref}
        style={{ ...customStyles, ...style }}
        disabled={disabled || loading}
        onClick={handleClick}
        {...props}
      >
        {wantsIosOverlay && <IosHapticOverlay disabled={disabled || loading} />}
        {loading && (
          <svg
            className="mr-2 h-4 w-4 animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
        )}
        {icon && iconPosition === 'left' && (
          <span className={showText ? "mr-2" : ""}>
            <FontAwesomeIcon icon={icon} />
          </span>
        )}
        {showText && children}
        {icon && iconPosition === 'right' && (
          <span className={showText ? "ml-2" : ""}>
            <FontAwesomeIcon icon={icon} />
          </span>
        )}
      </Comp>
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
